import anaConsts from 'analytics/constants';

const { PAGE_TYPES } = anaConsts;

/**
 * GIM-5668 (E2-9 [SOT]): builds the SOT `customPayload` map for the address-verification sotTypes,
 * keyed `[page].addressValidation.[attribute]` (SOT-78) with Loqate fields under
 * `providerMetadata.loqate.*`. Callers must flag-gate on `avsLoqateV2`; flag-off emits no customPayload.
 */

// SOT-78 page namespaces, keyed by analytics pageType.
const PAGE_NAMESPACE_BY_PAGE_TYPE = {
    [PAGE_TYPES.CHECKOUT]: 'checkout',
    [PAGE_TYPES.USER_PROFILE]: 'profile',
    [PAGE_TYPES.MY_ACCOUNT_2]: 'profile',
    [PAGE_TYPES.REPLACEMENT_ORDER]: 'orderEdit'
};

/**
 * Derives the SOT-78 page namespace from an analytics pageType.
 *
 * @param {string} pageType - Analytics pageType (see anaConsts.PAGE_TYPES).
 * @returns {string} The page namespace (`checkout`, `profile`, or `orderEdit`); unmapped pageTypes
 *   fall back to `checkout` (the dominant entry point).
 */
const getAvsPageNamespace = pageType => PAGE_NAMESPACE_BY_PAGE_TYPE[pageType] || 'checkout';

/**
 * Formats an AVS recommendedAddress into the single-line string the SOT template expects.
 *
 * @param {Object} [addr] - Recommended address (address1, address2, city, state, postalCode).
 * @returns {?string} A `street, city, state zip` line, or null when no populated fields are present.
 */
const formatRecommendedAddress = addr => {
    if (!addr) {
        return null;
    }

    const street = [addr.address1, addr.address2].filter(Boolean).join(' ').trim();
    const region = [addr.state, addr.postalCode].filter(Boolean).join(' ').trim();
    const parts = [street, addr.city, region].filter(Boolean);

    return parts.length ? parts.join(', ') : null;
};

/**
 * Builds the flat, dotted `customPayload` for an address-verification SOT event. Values are stringified
 * (booleans → "true"/"false"); null/undefined/empty sources are omitted so we never ship perpetual nulls.
 *
 * @param {Object} [params] - Builder inputs.
 * @param {string} [params.pageType] - Analytics pageType used to derive the namespace.
 * @param {boolean} [params.addressValidated] - Whether the address passed Loqate verification.
 * @param {boolean} [params.recommended] - Whether Loqate returned a different suggestion.
 * @param {boolean} [params.userOverride] - Whether the shopper kept their entered address.
 * @param {string} [params.recommendedAddress] - Pre-formatted Loqate-suggested address line.
 * @param {Object} [params.auxiliaryAttributes] - Loqate response metadata (DPV/CASS/SERP fields).
 * @returns {Object.<string, string>} The namespaced customPayload map (empty when nothing is supplied).
 */
const buildAvsCustomPayload = ({
    pageType, addressValidated, recommended, userOverride, recommendedAddress, auxiliaryAttributes
} = {}) => {
    const ns = `${getAvsPageNamespace(pageType)}.addressValidation`;
    const payload = {};

    // Keep boolean false; drop null/undefined/empty.
    const put = (key, value) => {
        if (value === null || value === undefined || value === '') {
            return;
        }

        payload[key] = String(value);
    };

    // Common attribute leaves.
    put(`${ns}.validated`, addressValidated);
    put(`${ns}.recommended`, recommended);
    put(`${ns}.userOverride`, userOverride);
    put(`${ns}.recommendedAddress`, recommendedAddress);

    const aux = auxiliaryAttributes || {};
    put(`${ns}.residentialDelivery`, aux.residentialDelivery);

    // Vendor-specific Loqate fields (providerMetadata.loqate.*).
    put(`${ns}.providerMetadata.loqate.avcScore`, aux.avc);
    // US DPV / CASS.
    put(`${ns}.providerMetadata.loqate.returnCode`, aux.returnCode);
    // SOT key is `enhancedReturnedCode` (extra "ed") per ticket; AVS source field is `enhancedReturnCode`.
    put(`${ns}.providerMetadata.loqate.enhancedReturnedCode`, aux.enhancedReturnCode);
    put(`${ns}.providerMetadata.loqate.dpvConfirmedIndicator`, aux.dpvConfirmedIndicator);
    put(`${ns}.providerMetadata.loqate.dpvFootnotesCarrierRoute`, aux.carrierRoute);
    put(`${ns}.providerMetadata.loqate.dpvFootnotesCmraIndicator`, aux.cmraIndicator);
    put(`${ns}.providerMetadata.loqate.dpvFootnotesDefaultFlag`, aux.defaultFlag);
    put(`${ns}.providerMetadata.loqate.dpvFootnotesDropSiteIndicator`, aux.dropSiteIndicator);
    put(`${ns}.providerMetadata.loqate.dpvFootnotesNoStatReason`, aux.noStatReason);
    put(`${ns}.providerMetadata.loqate.dpvFootnotesNsl`, aux.nsl);
    // CA SERP.
    put(`${ns}.providerMetadata.loqate.serpStatusEx`, aux.serpStatusEx);
    put(`${ns}.providerMetadata.loqate.serpResultCode`, aux.result);

    return payload;
};

export {
    getAvsPageNamespace, formatRecommendedAddress, buildAvsCustomPayload
};
